import React, { useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { PieChart } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { FundraiserService } from "../../../services";
import { IAPIFundraiser } from "../../../interfaces";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { AuthService, Loader } from "@mindmemobile/mm-shared-global";
import { CreateFundraiserModel } from "../../../models";

const InsightsContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const InsightsTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  text-align: center;
`;

const ChartSectionTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
`;

const PieChartContainer = styled.div`
  position: relative;
  width: 280px;
  height: 280px;
  margin: 0 auto 20px;
`;

const PieChartSvg = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const ChartLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1e293b;
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

const EmptyChartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
  min-height: 300px;
`;

const EmptyChartIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 20px;
`;

const EmptyChartTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyChartText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

interface DonationInsightsSectionProps {
  fundraiser: CreateFundraiserModel;
  fundraiserId?: string;
}
interface IRanges {
  min: number;
  max: number;
  label: string;
  color: string;
  percentage: number;
}

interface IPaymentMethodBreakdown {
  method: string;
  count: number;
  percentage: number;
}

interface HasPercentage {
  percentage: number;
}

interface PieSlice {
  pathData: string;
  startAngle: number;
  endAngle: number;
}

export const DonationInsightsSection: React.FC<DonationInsightsSectionProps> =
  observer(({ fundraiser, fundraiserId }) => {
    const [ranges, setRanges] = React.useState<IRanges[]>([]);
    const [paymentMethodBreakDowns, setPaymentMethodBreakDowns] =
      React.useState<IPaymentMethodBreakdown[]>([]);
    const [showLoader, setShowLoader] = React.useState<boolean>(true);

    useEffect(() => {
      FundraiserService.getDonationInsights(
        AuthService.authData.accountId,
        fundraiser.id
      ).subscribe({
        next: (response) => {
          const mappedRanges =
            response.result.amountInsights?.map((range, idx) => ({
              min: range.startValue,
              max: range.endValue || Infinity,
              label: `$${range.startValue} - $${range.endValue || Infinity}`,
              color: getRangeColor(idx),
              percentage: range.percentage,
            })) || [];

          const mappedPaymentMethods =
            response.result.paymentMethodInsights?.map((method) => ({
              method: method.brand,
              count: 0, // Placeholder, actual count calculation can be added if needed
              percentage: method.percentage,
            })) || [];
          setRanges(mappedRanges);
          setPaymentMethodBreakDowns(mappedPaymentMethods);
          setShowLoader(false);
        },
      });
    }, [fundraiser.id]);

    function getRangeColor(index: number) {
      const colors = ["#FFD1DC", "#FFABAB", "#FFC3A0", "#FFF3B0", "#A7DBD8"];
      return colors[index % colors.length];
    }

    // Calculate donation range breakdown with 5 ranges
    // const getDonationRangeBreakdown = () => {
    //   const amounts = fundraiserStore.getDonationAmountsForFundraiser(
    //     fundraiser.id
    //   );
    //   if (amounts.length === 0) return [];

    //   // // Create exactly 5 ranges with new breakpoints
    //   // const ranges: IRanges[] = [
    //   //   { min: 1, max: 99, label: '$1 - $99', color: '#FFD1DC' },
    //   //   { min: 100, max: 499, label: '$100 - $499', color: '#FFABAB' },
    //   //   { min: 500, max: 999, label: '$500 - $999', color: '#FFC3A0' },
    //   //   { min: 1000, max: 2499, label: '$1,000 - $2,499', color: '#FFF3B0' },
    //   //   { min: 2500, max: Infinity, label: '$2,500+', color: '#A7DBD8' }
    //   // ];

    //   const result = ranges.map((range) => {
    //     const count = amounts.filter(
    //       (amount) => amount >= range.min && amount <= range.max
    //     ).length;
    //     const percentage =
    //       amounts.length > 0 ? (count / amounts.length) * 100 : 0;
    //     return {
    //       range: range.label,
    //       count,
    //       percentage,
    //       color: range.color,
    //     };
    //   });

    //   return result;
    // };

    // Get payment method breakdown
    const getPaymentMethodBreakdown = () => {
      return fundraiserStore.getPaymentMethodBreakdownForFundraiser(
        fundraiser.id
      );
    };

    // Colors for payment methods
    const getPaymentMethodColor = (method: string) => {
      method = method.toLowerCase();
      const colors: Record<string, string> = {
        visa: "#4A90E2",
        mastercard: "#E966A0",
        amex: "#2D9CDB",
        discover: "#F55C7A",
        ach: "#27AE60",
        other: "#8D99AE",
      };
      return colors[method] || "#64748b";
    };

    const createPieSlices = <T extends HasPercentage>(
      data: T[]
    ): PieSlice[] => {
      if (!data || data.length === 0) return [];

      //const filtered = data.filter((item) => item.percentage > 0);
      const centerX = 140;
      const centerY = 140;
      const radius = 120;

      // Special case: only one item with 100%
      if (data.length === 1 && data[0].percentage === 100) {
        const pathData = [
          `M ${centerX} ${centerY}`,
          `L ${centerX + radius} ${centerY}`,
          `A ${radius} ${radius} 0 1 1 ${centerX - radius} ${centerY}`,
          `A ${radius} ${radius} 0 1 1 ${centerX + radius} ${centerY}`,
          "Z",
        ].join(" ");

        return [{ pathData, startAngle: 0, endAngle: 360 }];
      }

      let cumulativePercentage = 0;

      return data.map((item) => {
        const startAngle = (cumulativePercentage / 100) * 360;
        const endAngle = ((cumulativePercentage + item.percentage) / 100) * 360;
        cumulativePercentage += item.percentage;

        const startAngleRad = (startAngle * Math.PI) / 180;
        const endAngleRad = (endAngle * Math.PI) / 180;

        const x1 = centerX + radius * Math.cos(startAngleRad);
        const y1 = centerY + radius * Math.sin(startAngleRad);
        const x2 = centerX + radius * Math.cos(endAngleRad);
        const y2 = centerY + radius * Math.sin(endAngleRad);

        const largeArcFlag = item.percentage > 50 ? 1 : 0;

        const pathData = [
          `M ${centerX} ${centerY}`,
          `L ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          "Z",
        ].join(" ");

        return { pathData, startAngle, endAngle };
      });
    };

    const donationRangeData = ranges.filter(x => x.percentage > 0); //getDonationRangeBreakdown();
    const paymentMethodData = paymentMethodBreakDowns.filter(x => x.percentage > 0); //getPaymentMethodBreakdown(); 
    const donationRangeSlices = createPieSlices(donationRangeData);
    const paymentMethodSlices = createPieSlices(paymentMethodData);

    function renderCharts() {
      if (showLoader) {
        return new Loader().loaderComponent;
      }
      return (
        <>
          {" "}
          {/* Donation Range Pie Chart */}
          <ChartSection>
            <ChartSectionTitle>Donation Breakdown</ChartSectionTitle>
            {donationRangeData.length === 0 ? (
              <EmptyChartState>
                <EmptyChartIcon>
                  <PieChart size={40} />
                </EmptyChartIcon>
                <EmptyChartTitle>No Donation Data</EmptyChartTitle>
                <EmptyChartText>
                  Donation amount breakdown will appear
                  <br />
                  here once donations are received.
                  <br />
                  <br />
                </EmptyChartText>
              </EmptyChartState>
            ) : (
              <>
                <PieChartContainer>
                  <PieChartSvg viewBox="0 0 280 280">
                    {donationRangeSlices.map((slice, index) => (
                      <path
                        key={index}
                        d={slice.pathData}
                        fill={donationRangeData[index].color}
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                    ))}
                  </PieChartSvg>
                </PieChartContainer>
                <ChartLegend>
                  {donationRangeData.map((item, index) => (
                    <LegendItem key={index}>
                      <LegendColor color={item.color} />
                      <span>
                        ${item.min} - ${item.max} ({item.percentage.toFixed(1)}
                        %)
                      </span>
                    </LegendItem>
                  ))}
                </ChartLegend>
              </>
            )}
          </ChartSection>
          {/* Payment Method Pie Chart */}
          <ChartSection>
            <ChartSectionTitle>Payment Methods</ChartSectionTitle>
            {paymentMethodData.length === 0 ? (
              <EmptyChartState>
                <EmptyChartIcon>
                  <PieChart size={40} />
                </EmptyChartIcon>
                <EmptyChartTitle>No Payment Data</EmptyChartTitle>
                <EmptyChartText>
                  Payment method breakdown will appear
                  <br />
                  here once donations are received.
                  <br />
                  <br />
                </EmptyChartText>
              </EmptyChartState>
            ) : (
              <>
                <PieChartContainer>
                  <PieChartSvg viewBox="0 0 280 280">
                    {paymentMethodSlices.map((slice, index) => (
                      <path
                        key={index}
                        d={slice.pathData}
                        fill={getPaymentMethodColor(
                          paymentMethodData[index].method
                        )}
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                    ))}
                  </PieChartSvg>
                </PieChartContainer>
                <ChartLegend>
                  {paymentMethodData
                    .sort((a, b) => b.percentage - a.percentage)
                    .map((item, index) => (
                      <LegendItem key={index}>
                        <LegendColor
                          color={getPaymentMethodColor(item.method)}
                        />
                        <span>
                          {item.method} ({item.percentage.toFixed(1)}%)
                        </span>
                      </LegendItem>
                    ))}
                </ChartLegend>
              </>
            )}
          </ChartSection>
        </>
      );
    }
    return (
      <InsightsContainer>
        <InsightsTitle>
          <PieChart size={28} color="#8b5cf6" />
          Donation Insights
        </InsightsTitle>

        <ChartsGrid>{renderCharts()}</ChartsGrid>
      </InsightsContainer>
    );
  });
